import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, RefreshCw, Inbox, Gift, GraduationCap, Briefcase, Download } from "lucide-react";
import { downloadAdminCsv } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  "referral":            { label: "Parent Referral",          icon: Gift,          tone: "yellow" },
  "private-coaching":    { label: "Private 1-to-1 Coaching",  icon: GraduationCap, tone: "purple" },
  "career-application":  { label: "Career Application",       icon: Briefcase,     tone: "yellow" },
};

const Pill = ({ tone = "zinc", children }) => {
  const tones = {
    yellow: "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/40",
    purple: "bg-brand-purpleLight/15 text-brand-purpleLight border-brand-purpleLight/40",
    zinc:   "bg-white/5 text-zinc-300 border-white/15",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-[0.2em] ${tones[tone] || tones.zinc}`}>
      {children}
    </span>
  );
};

export const AdminEnquiries = ({ pw }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (kindFilter) params.kind = kindFilter;
      if (dateFrom)   params.date_from = dateFrom;
      if (dateTo)     params.date_to = dateTo;
      const r = await axios.get(`${API}/admin/enquiries`, {
        headers: { "x-admin-password": pw },
        params,
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load enquiries");
    } finally {
      setLoading(false);
    }
  }, [pw, kindFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => downloadAdminCsv({
    endpoint: "/admin/enquiries/export.csv",
    params: { kind: kindFilter, date_from: dateFrom, date_to: dateTo },
    pw,
    filename: `usdd-enquiries-${kindFilter || "all"}`,
  });

  return (
    <div data-testid="admin-enquiries">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setKindFilter("")}
            data-testid="enq-filter-all"
            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all ${kindFilter === "" ? "bg-brand-yellow text-black" : "bg-white/5 border border-white/15 text-zinc-300 hover:border-brand-yellow/50"}`}
          >
            All ({items.length})
          </button>
          {Object.entries(KIND_META).map(([k, meta]) => (
            <button
              key={k}
              onClick={() => setKindFilter(k)}
              data-testid={`enq-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all ${kindFilter === k ? "bg-brand-yellow text-black" : "bg-white/5 border border-white/15 text-zinc-300 hover:border-brand-yellow/50"}`}
            >
              {meta.label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="enq-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 text-[10px] font-bold uppercase tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow disabled:opacity-50"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Date range + CSV export row */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
          From
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="enq-date-from"
            className="bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-white text-xs outline-none focus:border-brand-yellow"
          />
        </label>
        <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
          To
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="enq-date-to"
            className="bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-white text-xs outline-none focus:border-brand-yellow"
          />
        </label>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(""); setDateTo(""); }}
            className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 hover:text-brand-yellow"
            data-testid="enq-clear-dates"
          >
            Clear dates ×
          </button>
        )}
        <button
          onClick={exportCsv}
          disabled={loading}
          data-testid="enq-export-csv"
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-brand-yellow text-black text-[10px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover disabled:opacity-50 transition-colors"
        >
          <Download size={11} /> Export filtered CSV
        </button>
      </div>

      {items.length === 0 && !loading && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center">
          <Inbox size={26} className="text-brand-yellow mx-auto mb-3" />
          <div className="text-sm text-zinc-300">No enquiries yet.</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">
            Referrals + private coaching submissions will appear here.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const meta = KIND_META[it.kind] || { label: it.kind, icon: Inbox, tone: "zinc" };
          const Icon = meta.icon;
          const p = it.payload || {};
          return (
            <div
              key={it.id}
              data-testid={`enq-row-${it.id}`}
              className="rounded-2xl border border-white/10 bg-white/[0.03] hover:border-white/20 transition-colors p-5"
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-lg bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} className="text-brand-yellow" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill tone={meta.tone}>{meta.label}</Pill>
                    <Pill>{(it.created_at || "").slice(0, 16).replace("T", " ")}</Pill>
                  </div>
                  <div className="font-display text-lg text-white tracking-wide mt-1 leading-tight">
                    {p.new_parent_name || p.name || "—"}
                    {p.new_child_name && <span className="text-zinc-500 text-sm"> · {p.new_child_name}</span>}
                  </div>
                  <div className="mt-1 text-xs text-zinc-400 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1"><Mail size={11} className="text-zinc-500" /> {p.email || "—"}</span>
                    <span className="inline-flex items-center gap-1"><Phone size={11} className="text-zinc-500" /> {p.phone || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                {it.kind === "referral" && (
                  <>
                    <Field k="Referring parent" v={p.referring_parent_name} />
                    <Field k="Referring child"  v={p.referring_child_name} />
                    <Field k="Preferred class"  v={p.preferred_class} />
                    <Field k="Preferred loc."   v={p.preferred_location} />
                    {p.notes && <Field k="Notes" v={p.notes} full />}
                  </>
                )}
                {it.kind === "private-coaching" && (
                  <>
                    <Field k="Coaching type" v={p.coaching_type} />
                    <Field k="Delivery"      v={p.delivery} />
                    <Field k="Location"      v={p.preferred_location} />
                    <Field k="Days / times"  v={p.preferred_days} />
                    <Field k="Experience"    v={p.experience} />
                    <Field k="# people"      v={p.num_people} />
                    <Field k="Budget"        v={p.budget} />
                    {(p.age || p.guardian_name) && (
                      <Field k="Age / guardian" v={`${p.age || "—"}${p.guardian_name ? ` · ${p.guardian_name}` : ""}`} />
                    )}
                    {p.goal && <Field k="Goal" v={p.goal} full />}
                  </>
                )}
                {it.kind === "career-application" && (
                  <>
                    <Field k="Track"            v={p.track} />
                    <Field k="Age"              v={p.age} />
                    <Field k="Location"         v={p.location} />
                    <Field k="Preferred start"  v={p.preferred_start_date} />
                    {p.dance_experience && <Field k="Dance experience" v={p.dance_experience} full />}
                    {p.why_applying     && <Field k="Why applying"     v={p.why_applying}     full />}
                  </>
                )}
              </div>

              {p.utm && (
                <div className="mt-3 text-[11px] text-zinc-400 bg-brand-purpleDeep/15 border border-brand-purpleLight/25 rounded-xl p-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-brand-purpleLight font-bold">Attribution</span>
                  {p.utm.utm_source && <span><strong className="text-white">source:</strong> {p.utm.utm_source}</span>}
                  {p.utm.utm_medium && <span><strong className="text-white">medium:</strong> {p.utm.utm_medium}</span>}
                  {p.utm.utm_campaign && <span><strong className="text-white">campaign:</strong> {p.utm.utm_campaign}</span>}
                  {p.utm.ref && <span><strong className="text-white">ref:</strong> {p.utm.ref}</span>}
                  {p.utm.focus && <span><strong className="text-white">focus:</strong> {p.utm.focus}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Field = ({ k, v, full = false }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{k}</div>
    <div className="text-sm text-white mt-0.5 break-words">{v || "—"}</div>
  </div>
);
